import {
    api,
    type ListProjectFundsParams,
    type ProjectFundInfoResponse,
    type ProjectFundUserInfoCreateRequest,
    type ProjectFundWellInfo,
} from '@/api/api.v2';
import type { DropdownOptions, WellForm } from './types';
import request from '@/api/request';
import { useSelectedItem } from '@/components/WellLoggingOperations/Inspector/CurveMeta/CurveMeta';
import { VALIDATION_KEYS_MAP } from './constants';
import type { RootState } from '@/components/WellLoggingOperations/types';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';

type ColumnDef = {
    headerName: string;
    field: string;
    width?: number;
    valueGetter?: (params: { data: any }) => any;
    valueFormatter?: (params: { value: any; data: any }) => any;
};

export const mapToDropdownOptions = (data: ProjectFundInfoResponse): DropdownOptions => {
    return {
        stratums: data.stratums.map(s => ({
            label: s.stratumTitle,
            value: s.oilFieldId,
        })),
        mines: data.mines.map(m => ({
            label: m.number,
            value: m.id,
        })),
        kupols: data.kupols.map(k => ({
            label: k.title,
            value: k.id,
        })),
        areas: data.areas.map(a => ({
            label: a.title,
            value: a.id,
        })),
        licenseZones: data.licenseZones.map(l => ({
            label: l.title,
            value: l.id,
        })),
        wellbores: data.wellbores.map(w => ({
            label: w.title,
            value: w.id,
        })),
        wellTypes: data.wellTypes.map(wt => ({
            label: wt.title,
            value: wt.id,
            iconId: wt.iconId,
        })),
    };
};

export const isInteractive = (e: Event | null | undefined) => {
    const el = e?.target as HTMLElement | null;
    if (!el) return false;
    return !!el.closest(
        [
            'input',
            'textarea',
            'select',
            'button',
            '.ant-select',
            '.ant-select-selector',
            '.ant-select-dropdown',
            '.ant-input',
            '.ant-checkbox',
            '.ant-radio',
            '.ant-switch',
            '.ant-picker',
            '.ant-slider',
            '.ant-pagination',
            '.ant-dropdown',
        ].join(','),
    );
};

export const exportExcel = async (path: string, filename: string) => {
    try {
        const blob = await request.get(path).blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    } catch (err) {
        console.error('Ошибка при экспорте Excel:', err);
    }
};

export const useItemParams = () => {
    const params: ListProjectFundsParams = {};
    const item = useSelectedItem();

    if (item?.node === 'field') {
        params.fieldId = item.id;
    }
    if (item?.fieldId) {
        params.fieldId = item.fieldId as number;
    } else if (item?.parentVentureId) {
        if (item?.parentVentureNode === 'field') {
            params.fieldId = item.parentVentureId as number;
        }
        if (item?.node === 'company') {
            params.ventureId = item.id as number;
        }
    }
    return params;
};

export const normalizeParams = (obj: ListProjectFundsParams) => ({
    ventureId: obj.ventureId ?? null,
    fieldId: obj.fieldId ?? null,
    areaId: obj.areaId ?? null,
    mineId: obj.mineId ?? null,
    kupolId: obj.kupolId ?? null,
});

export const validateWellInfo = (well: WellForm) => {
    const errors: Partial<Record<keyof WellForm, boolean>> = {};
    
    errors.oilFieldId = well.oilFieldId === null && well.stratum === null;
    errors.wellboreValuesId = well.wellboreValuesId === null;
    errors.developmentIconId = well.developmentIconId === null;
    // errors.explorationZone =
    //     well.explorationZone === null && (well.explorationZoneId === null || well.explorationZoneId === undefined);
    errors.stratumIntersectX = well.stratumIntersectX === null || Number.isNaN(well.stratumIntersectX);
    errors.stratumIntersectY = well.stratumIntersectY === null || Number.isNaN(well.stratumIntersectY);

    return errors;
};

export const useAdditionalOptions = (propKey?: keyof ProjectFundWellInfo) => {
    const { customOptionsMap, activeProjectFond } = useSelector((state: RootState) => state.projectFund);
    const customOptions = customOptionsMap[activeProjectFond ?? 0];

    const additionalOptionsKey = propKey
        ? (VALIDATION_KEYS_MAP[propKey] as keyof Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>)
        : null;
    return (
        customOptions && additionalOptionsKey && customOptions[additionalOptionsKey]
            ? customOptions[additionalOptionsKey]
            : []
    ).map(({ value }) => ({ value, label: value, custom: true }));
};

export const getCustomOptionsDiff = (
    existing: Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>,
    incoming: Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>,
): Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'> => {
    const diff: any = {};

    for (const key of Object.keys(incoming) as Array<keyof typeof incoming>) {
        const newArr = incoming[key] ?? [];
        const oldArr = existing?.[key] ?? [];

        // сравнение по value (+ fieldId, если есть)
        const added = newArr.filter(
            (item: any) =>
                !oldArr.some(
                    (exist: any) =>
                        exist.value === item.value && (item.fieldId ? exist.fieldId === item.fieldId : true),
                ),
        );

        if (added.length > 0) diff[key] = added;
    }

    return diff;
};

export const hasAnyOptions = (options: Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>) => {
    return Object.values(options).some(arr => Array.isArray(arr) && arr.length > 0);
};

/**
 * Экспорт нескольких проектных фондов — по каждому создаётся отдельный Excel-файл
 * @param fundIds список id фондов
 * @param columns схема колонок (например, WELL_COLUMNS)
 */
export async function exportProjectFundsSeparately(
    fundIds: Array<{ id: number; fileName: string }>,
    columns: Array<ColDef<ProjectFundWellInfo> | ColGroupDef<ProjectFundWellInfo>>,
) {
    for (const { id: fundId, fileName } of fundIds) {
        // Загружаем детали фонда и список скважин
        const { projectFundWells } = await api.map.projectFund.details(fundId);
        // Получаем справочники (чтобы заменить id → названия)
        const optionsMap = await api.map.projectFund.getCustomOptions({ projectFundId: fundId });
        await fetchAllInfo(projectFundWells, optionsMap);

        // Плоский список колонок (с раскрытием групп)
        const flatColumns: ColDef<ProjectFundWellInfo>[] = [];
        const flatten = (cols: Array<ColDef<ProjectFundWellInfo> | ColGroupDef<ProjectFundWellInfo>>) => {
            for (const col of cols) {
                if ('children' in col && col.children) flatten(col.children);
                else if ('field' in col && col.field) flatColumns.push(col);
            }
        };
        flatten(columns);

        // Заголовки
        const headers = flatColumns.map(col => col.headerName || col.field || '');

        // Строки
        const rows = projectFundWells.map((row, rowIndex) =>
            flatColumns.map((col, colIndex) => {
                let value: any = row[(col.field ?? '') as keyof ProjectFundWellInfo];

                // 1. valueGetter
                if (col.valueGetter) {
                    const params = { data: row } as any;
                    value = col.valueGetter(params);
                }

                // 2. valueFormatter
                if (col.valueFormatter) {
                    const params: ValueFormatterParams = { value, data: row } as any;
                    value = col.valueFormatter(params);
                }

                // 3. Заменяем id на отображаемое значение
                if (col.field && typeof value === 'number' && optionsMap) {
                    const key = row.fieldId ?? row.explorationZoneId;
                    switch (col.field) {
                        case 'mineId':
                            value = optionsMap[key]?.mines?.find((o: any) => o.id === value)?.number ?? value;
                            break;
                        case 'areaId':
                            value = optionsMap[key]?.areas?.find((o: any) => o.id === value)?.title ?? value;
                            break;
                        case 'oilFieldId':
                            value =
                                optionsMap[key]?.stratums?.find((o: any) => o.oilFieldId === value)?.stratumTitle ??
                                value;
                            break;
                        case 'licenseZoneId':
                            value = optionsMap[key]?.licenseZones?.find((o: any) => o.id === value)?.title ?? value;
                            break;
                        case 'wellboreValuesId':
                            value = optionsMap[key]?.wellbores?.find((o: any) => o.id === value)?.title ?? value;
                            break;
                        case 'developmentIconId':
                            value = optionsMap[key]?.wellTypes?.find((o: any) => o.id === value)?.title ?? value;
                            break;
                    }
                }

                // 4. Форматирование даты
                if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
                    value = dayjs(value).format('DD.MM.YYYY HH:mm');
                }

                if (col.field === 'index') {
                    value = rowIndex + 1;
                }

                return value ?? '';
            }),
        );

        // Формируем лист и задаём ширину
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws['!cols'] = flatColumns.map(col => ({
            wch: col.width ? Math.round(col.width / 6.5) : 15,
        }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Данные');
        const fullName = fileName
            ? `${projectFundWells[0].FieldTitle ?? ''}  Проектный фонд ${fileName}`
            : `Проектный фонд ${fundId}`;
        // Сохраняем отдельный файл
        const safeName = sanitizeFileName(fullName);
        XLSX.writeFile(wb, `${safeName}.xlsx`);
    }
}

async function fetchAllInfo(wells: ProjectFundWellInfo[], optionsMap: Record<string, any>): Promise<void> {
    const seenKeys = new Set<string>();
    const promises: Promise<void>[] = [];

    for (const well of wells) {
        const key = well.fieldId ?? well.explorationZoneId;
        if (!key) continue;
        if (optionsMap[key]) continue;
        if (seenKeys.has(String(key))) continue;

        seenKeys.add(String(key));

        const params: { fieldId?: number; explorationZoneId?: number } = {};
        if (well.fieldId) params.fieldId = well.fieldId;
        //if (well.explorationZoneId) params.explorationZoneId = well.explorationZoneId;

        promises.push(
            api.map.projectFund.info(params).then(data => {
                // Сохраняем результат сразу в optionsMap
                optionsMap[key] = data;
            }),
        );
    }

    await Promise.all(promises);
}

function sanitizeFileName(name: string) {
    return name
        .replace(/[\\/:*?"<>|]/g, '')
        .trim()
        .slice(0, 100);
}
